/**
 * SCR-102: Document List View
 * BRD-001 | US-002 | Journey Phase: Daily Use
 * Personas: retail_consumer, gemachtigde, professional
 *
 * ISO 27001 Controls: A.8.2.1, A.9.4.1, A.12.4.1, A.12.4.3, A.18.1.4
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  PageHeader,
  Button,
  Input,
  Select,
  DataTable,
  Badge,
  Icon,
  EmptyState,
  Spinner,
  ConfirmDialog,
  useConfirmDialog,
  Card,
  SplitPane,
  ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  type Column,
  type SelectOption,
} from '@myvault/shared';
import { DocumentUploadModal } from './DocumentUploadModal';
import { SecureDocumentUploadModal } from './SecureDocumentUploadModal';
import { ShareDocumentModal } from './ShareDocumentModal';
import { DocumentDetailPanel } from './DocumentDetailPanel';
import { DelegateFromDocumentModal } from './DelegateFromDocumentModal';
import {
  useDocuments,
  useCategories,
  useDeleteDocument,
  useDeleteDocuments,
  type DocumentListParams,
} from '../hooks/useDocuments';
import type { Document } from '@myvault/shared';

// Sort options - matches SCR-102 specification
const SORT_OPTIONS: SelectOption[] = [
  { value: 'created_at:desc', label: 'Nieuwste eerst' },
  { value: 'created_at:asc', label: 'Oudste eerst' },
  { value: 'title:asc', label: 'Naam (A-Z)' },
  { value: 'title:desc', label: 'Naam (Z-A)' },
  { value: 'size:desc', label: 'Grootste eerst' },
  { value: 'size:asc', label: 'Kleinste eerst' },
];

// File type filter options - matches SCR-102 specification
const FILE_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Alle types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'Word' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'jpg,jpeg,png', label: 'Afbeelding' },
];

// Date range options - matches SCR-102 specification
const DATE_RANGE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Alle datums' },
  { value: 'today', label: 'Vandaag' },
  { value: 'week', label: 'Afgelopen week' },
  { value: 'month', label: 'Afgelopen maand' },
  { value: 'quarter', label: 'Afgelopen kwartaal' },
  { value: 'year', label: 'Afgelopen jaar' },
];

// Page size options
const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '10', label: '10 per pagina' },
  { value: '20', label: '20 per pagina' },
  { value: '50', label: '50 per pagina' },
  { value: '100', label: '100 per pagina' },
];

// View mode type
type ViewMode = 'list' | 'grid';

// Category icon mapping
const CATEGORY_ICONS: Record<string, string> = {
  all: 'folder',
  insurance: 'shieldCheck',
  mortgage: 'home',
  tax: 'documentText',
  legal: 'scale',
  medical: 'heart',
  other: 'folder',
};

// =============================================================================
// DocumentCard Component for Grid View
// =============================================================================

interface DocumentCardProps {
  document: Document;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
  onView: (doc: Document) => void;
  onShare: (doc: Document) => void;
  onDelegate: (doc: Document) => void;
  onDelete: (doc: Document) => void;
}

function DocumentCard({
  document: doc,
  isSelected,
  onSelect,
  onView,
  onShare,
  onDelegate,
  onDelete,
}: DocumentCardProps) {
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get file type icon
  const getFileIcon = (
    mimeType: string
  ): 'documentText' | 'photo' | 'document' | 'tableCells' => {
    if (mimeType.includes('pdf')) return 'documentText';
    if (mimeType.includes('image')) return 'photo';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'tableCells';
    return 'document';
  };

  // Get classification badge variant
  const getClassificationVariant = (
    classification: string
  ): 'default' | 'secondary' | 'warning' | 'danger' => {
    switch (classification) {
      case 'public':
        return 'secondary';
      case 'internal':
        return 'default';
      case 'confidential':
        return 'warning';
      case 'restricted':
        return 'danger';
      default:
        return 'default';
    }
  };

  // Check for sharing info
  const docWithSharing = doc as Document & { is_shared?: boolean; shared_count?: number };
  const isShared = docWithSharing.is_shared;
  const sharedCount = docWithSharing.shared_count || 0;

  return (
    <Card
      className={`relative hover:shadow-md transition-shadow cursor-pointer ${
        isSelected ? 'ring-2 ring-mv-blue-500' : ''
      }`}
      onClick={() => onView(doc)}
    >
      {/* Selection checkbox */}
      <div
        className="absolute top-3 left-3 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(doc.id, e.target.checked)}
          className="h-4 w-4 rounded border-mv-gray-300 text-mv-blue-600 focus:ring-mv-blue-500"
          aria-label={`Selecteer ${doc.title}`}
        />
      </div>

      {/* Document icon */}
      <div className="flex justify-center pt-6 pb-4">
        <div className="w-16 h-16 rounded-lg bg-mv-gray-100 flex items-center justify-center">
          <Icon
            name={getFileIcon(doc.mime_type || '')}
            className="w-8 h-8 text-mv-gray-500"
          />
        </div>
      </div>

      {/* Document info */}
      <div className="px-4 pb-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-mv-gray-900 truncate flex-1" title={doc.title}>
            {doc.title}
          </h3>
          {isShared && (
            <Badge
              variant="secondary"
              className="text-xs flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 shrink-0"
            >
              <Icon name="userGroup" className="w-3 h-3" />
              {sharedCount > 1 ? sharedCount : ''}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-mv-gray-500 mb-3">
          <span>{formatFileSize(doc.size || 0)}</span>
          <span>•</span>
          <span>{formatDate(doc.created_at)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {doc.category && (
              <Badge variant="default" className="text-xs">
                {doc.category}
              </Badge>
            )}
            <Badge variant={getClassificationVariant(doc.classification)} className="text-xs">
              {doc.classification}
            </Badge>
          </div>
        </div>

        {/* Tags */}
        {doc.tags && doc.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {doc.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {doc.tags.length > 2 && (
              <Badge variant="default" className="text-xs">
                +{doc.tags.length - 2}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-mv-gray-100 px-4 py-2 flex items-center justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onShare(doc);
          }}
          aria-label={`Deel ${doc.title}`}
          title="Delen"
        >
          <Icon name="share" className="w-4 h-4 text-mv-blue-600" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelegate(doc);
          }}
          aria-label={`Machtig toegang voor ${doc.title}`}
          title="Toegang delegeren"
        >
          <Icon name="userPlus" className="w-4 h-4 text-purple-600" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(doc);
          }}
          aria-label={`Verwijder ${doc.title}`}
          title="Verwijderen"
        >
          <Icon name="trash" className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </Card>
  );
}

export function DocumentListView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm, dialogProps } = useConfirmDialog();

  // State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSecureUploadModalOpen, setIsSecureUploadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<Document | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // GAP-032: Delegate access from document view
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false);
  const [documentToDelegate, setDocumentToDelegate] = useState<Document | null>(null);

  // GAP-004: Desktop three-panel layout state
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1200);

  // GAP-004: Track screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1200;
      setIsDesktop(desktop);
      // Close detail panel on non-desktop
      if (!desktop) {
        setSelectedDocument(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GAP-004: Handle Escape key to close detail panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedDocument) {
        setSelectedDocument(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedDocument]);

  // Parse URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('limit') || '20', 10);
  const sortParam = searchParams.get('sort') || 'created_at:desc';
  const category = searchParams.get('category') || '';
  const fileType = searchParams.get('type') || '';
  const dateRange = searchParams.get('date') || '';

  // Parse sort param
  const [sortField, sortOrder] = sortParam.split(':') as [string, 'asc' | 'desc'];

  // Build query params for API
  const queryParams: DocumentListParams = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      sort: sortField,
      order: sortOrder,
      search: searchQuery,
      category: category || undefined,
      fileType: fileType || undefined,
      dateRange: dateRange || undefined,
    }),
    [currentPage, pageSize, sortField, sortOrder, searchQuery, category, fileType, dateRange]
  );

  // Fetch documents
  const {
    data: documentsData,
    isLoading,
    isError,
    refetch,
  } = useDocuments(queryParams);

  // Fetch categories
  const { data: categories } = useCategories();

  // Delete mutations
  const deleteDocument = useDeleteDocument();
  const deleteDocuments = useDeleteDocuments();

  const documents = documentsData?.documents || [];
  const pagination = documentsData?.pagination;
  const totalCount = pagination?.total || 0;
  const totalPages = pagination?.pages || 1;

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get file type icon (camelCase for Icon component)
  const getFileIcon = (
    mimeType: string
  ): 'documentText' | 'photo' | 'document' | 'tableCells' => {
    if (mimeType.includes('pdf')) return 'documentText';
    if (mimeType.includes('image')) return 'photo';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'tableCells';
    return 'document';
  };

  // Get classification badge variant
  const getClassificationVariant = (classification: string): 'default' | 'secondary' | 'warning' | 'danger' => {
    switch (classification) {
      case 'public':
        return 'secondary';
      case 'internal':
        return 'default';
      case 'confidential':
        return 'warning';
      case 'restricted':
        return 'danger';
      default:
        return 'default';
    }
  };

  // Update URL params
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Handle search
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      updateParams({ q: query, page: '1' });
    },
    [updateParams]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      updateParams({ sort: value, page: '1' });
    },
    [updateParams]
  );

  // Handle category change
  const handleCategoryChange = useCallback(
    (categoryId: string) => {
      updateParams({ category: categoryId === 'all' ? '' : categoryId, page: '1' });
    },
    [updateParams]
  );

  // Handle file type change
  const handleFileTypeChange = useCallback(
    (value: string) => {
      updateParams({ type: value, page: '1' });
    },
    [updateParams]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (value: string) => {
      updateParams({ date: value, page: '1' });
    },
    [updateParams]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (value: string) => {
      updateParams({ limit: value, page: '1' });
    },
    [updateParams]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      updateParams({ page: page.toString() });
    },
    [updateParams]
  );

  // Handle document actions
  // GAP-004: On desktop, show detail panel; on mobile/tablet, navigate to full page
  const handleViewDocument = useCallback(
    (doc: Document) => {
      if (isDesktop) {
        setSelectedDocument(doc);
      } else {
        navigate(`/documents/${doc.id}`);
      }
    },
    [navigate, isDesktop]
  );

  // GAP-004: Close detail panel
  const handleCloseDetailPanel = useCallback(() => {
    setSelectedDocument(null);
  }, []);

  const handleDeleteDocument = useCallback(
    (doc: Document) => {
      confirm({
        title: 'Document verwijderen',
        description: `Weet je zeker dat je "${doc.title}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
        confirmLabel: 'Verwijderen',
        variant: 'danger',
        onConfirm: async () => {
          await deleteDocument.mutateAsync(doc.id);
        },
      });
    },
    [confirm, deleteDocument]
  );

  // GAP-023: Handle share document action
  const handleShareDocument = useCallback((doc: Document) => {
    setDocumentToShare(doc);
    setIsShareModalOpen(true);
  }, []);

  // GAP-023: Handle bulk share
  const handleBulkShare = useCallback(() => {
    setDocumentToShare(null); // Clear single document, will use selectedIds
    setIsShareModalOpen(true);
  }, []);

  // GAP-032: Handle delegate access from document
  const handleDelegateDocument = useCallback((doc: Document) => {
    setDocumentToDelegate(doc);
    setIsDelegateModalOpen(true);
  }, []);

  // Handle bulk delete
  const handleBulkDelete = useCallback(() => {
    const count = selectedIds.size;
    confirm({
      title: `${count} documenten verwijderen`,
      description: `Weet je zeker dat je ${count} document(en) wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        await deleteDocuments.mutateAsync(Array.from(selectedIds));
        setSelectedIds(new Set());
      },
    });
  }, [selectedIds, confirm, deleteDocuments]);

  // Handle selection
  const handleSelectDocument = useCallback((docId: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(docId);
      } else {
        next.delete(docId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map((d) => d.id)));
    }
  }, [documents, selectedIds.size]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Upload complete handler
  const handleUploadComplete = useCallback(() => {
    refetch();
    setIsUploadModalOpen(false);
  }, [refetch]);

  // Secure upload complete handler
  const handleSecureUploadComplete = useCallback(() => {
    refetch();
    setIsSecureUploadModalOpen(false);
  }, [refetch]);

  // Table columns - matches SCR-102 item_structure
  const columns: Column<Document>[] = useMemo(
    () => [
      {
        key: 'title',
        header: 'Naam',
        sortable: true,
        render: (doc) => {
          // GAP-027: Check if document is shared (extended type from API)
          const docWithSharing = doc as Document & { is_shared?: boolean; shared_count?: number };
          const isShared = docWithSharing.is_shared;
          const sharedCount = docWithSharing.shared_count || 0;

          return (
            <div className="flex items-center gap-3">
              <Icon
                name={getFileIcon(doc.mime_type || '')}
                className="w-5 h-5 text-mv-gray-400 flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-mv-gray-900 truncate">{doc.title}</p>
                  {/* GAP-027: Show shared badge when document is shared */}
                  {isShared && (
                    <Badge
                      variant="secondary"
                      className="text-xs flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200"
                    >
                      <Icon name="userGroup" className="w-3 h-3" />
                      {sharedCount > 1 ? `Gedeeld (${sharedCount})` : 'Gedeeld'}
                    </Badge>
                  )}
                </div>
                {doc.tags && doc.tags.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {doc.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {doc.tags.length > 3 && (
                      <Badge variant="default" className="text-xs">+{doc.tags.length - 3}</Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: 'category',
        header: 'Categorie',
        render: (doc) =>
          doc.category ? (
            <Badge variant="default">{doc.category}</Badge>
          ) : (
            <span className="text-mv-gray-400">-</span>
          ),
      },
      {
        key: 'classification',
        header: 'Classificatie',
        render: (doc) => (
          <Badge variant={getClassificationVariant(doc.classification)}>
            {doc.classification}
          </Badge>
        ),
      },
      {
        key: 'size',
        header: 'Grootte',
        sortable: true,
        render: (doc) => (
          <span className="text-mv-gray-600">{formatFileSize(doc.size || 0)}</span>
        ),
      },
      {
        key: 'created_at',
        header: 'Geüpload',
        sortable: true,
        render: (doc) => (
          <span className="text-mv-gray-600">{formatDate(doc.created_at)}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: '180px',
        render: (doc) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDocument(doc);
              }}
              aria-label={`Open ${doc.title}`}
            >
              <Icon name="eye" className="w-4 h-4" />
            </Button>
            {/* GAP-023: Share action button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShareDocument(doc);
              }}
              aria-label={`Deel ${doc.title}`}
              title="Delen"
            >
              <Icon name="share" className="w-4 h-4 text-mv-blue-600" />
            </Button>
            {/* GAP-032: Delegate access action button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelegateDocument(doc);
              }}
              aria-label={`Machtig toegang voor ${doc.title}`}
              title="Toegang delegeren"
            >
              <Icon name="userPlus" className="w-4 h-4 text-purple-600" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDocument(doc);
              }}
              aria-label={`Verwijder ${doc.title}`}
            >
              <Icon name="trash" className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDocument, handleShareDocument, handleDelegateDocument, handleDeleteDocument]
  );

  // Check if filters are active
  const hasActiveFilters = searchQuery || category || fileType || dateRange;
  const isDeleting = deleteDocument.isPending || deleteDocuments.isPending;

  return (
    <div className="flex h-full">
      {/* Sidebar - matches SCR-102 sidebar section */}
      <aside
        className={`
          ${isSidebarOpen ? 'w-64' : 'w-0'}
          flex-shrink-0 border-r border-mv-gray-200 bg-white
          transition-all duration-200 overflow-hidden
          hidden md:block
        `}
      >
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-mv-gray-900">Categorieën</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Zijbalk sluiten"
            >
              <Icon name="xMark" className="w-4 h-4" />
            </Button>
          </div>

          {/* Category Tree - matches SCR-102 category_tree */}
          <nav aria-label="Documentcategorieën navigatie">
            <ul className="space-y-1">
              {categories?.map((cat) => {
                const isActive = category === cat.id || (cat.id === 'all' && !category);
                const iconName = CATEGORY_ICONS[cat.id] || 'folder';

                return (
                  <li key={cat.id}>
                    <button
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                        transition-colors duration-150
                        ${
                          isActive
                            ? 'bg-mv-blue-50 text-mv-blue-700 font-medium'
                            : 'text-mv-gray-700 hover:bg-mv-gray-50'
                        }
                      `}
                    >
                      <Icon name={iconName as 'folder'} className="w-4 h-4" />
                      <span className="flex-1 text-left">{cat.name}</span>
                      <span
                        className={`
                          text-xs px-2 py-0.5 rounded-full
                          ${isActive ? 'bg-mv-blue-100' : 'bg-mv-gray-100'}
                        `}
                      >
                        {cat.count}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Main Content - GAP-004: Use SplitPane on desktop when document is selected */}
      {isDesktop && selectedDocument ? (
        <SplitPane
          orientation="horizontal"
          defaultSize={65}
          minSize={40}
          maxSize={80}
          persistKey="document-list-split"
          className="flex-1"
        >
          <main className="h-full overflow-auto">
            <div className="p-6 space-y-6">
              {/* Header - matches SCR-102 header section */}
              <PageHeader
                title="Mijn Documenten"
                breadcrumbs={[
                  { label: 'Home', href: '/' },
                  { label: 'Documenten', href: '/documents' },
                ]}
                actions={
                  <div className="flex items-center gap-2">
                    {!isSidebarOpen && (
                      <Button
                        variant="ghost"
                        onClick={() => setIsSidebarOpen(true)}
                        className="md:hidden"
                      >
                        <Icon name="funnel" className="w-4 h-4 mr-2" />
                        Filters
                      </Button>
                    )}
                    {selectedIds.size > 0 && (
                      <Button
                        variant="secondary"
                        onClick={handleBulkDelete}
                        disabled={isDeleting}
                      >
                        <Icon name="trash" className="w-4 h-4 mr-2" />
                        Verwijderen ({selectedIds.size})
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setIsSecureUploadModalOpen(true)}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <Icon name="shieldCheck" className="w-4 h-4 mr-2" />
                      Veilig uploaden
                    </Button>
                    <Button variant="primary" onClick={() => setIsUploadModalOpen(true)}>
                      <Icon name="arrowUpTray" className="w-4 h-4 mr-2" />
                      Document uploaden
                    </Button>
                  </div>
                }
              />

              {/* Filters - matches SCR-102 filters section */}
              <Card className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Search */}
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      type="search"
                      placeholder="Zoek documenten..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      aria-label="Zoek in documenten"
                    />
                  </div>

                  {/* File Type Filter */}
                  <Select
                    options={FILE_TYPE_OPTIONS}
                    value={fileType}
                    onChange={(e) => handleFileTypeChange(e.target.value)}
                    aria-label="Filter op bestandstype"
                    className="w-36"
                  />

                  {/* Date Range Filter */}
                  <Select
                    options={DATE_RANGE_OPTIONS}
                    value={dateRange}
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                    aria-label="Filter op datumbereik"
                    className="w-40"
                  />

                  {/* Sort */}
                  <Select
                    options={SORT_OPTIONS}
                    value={sortParam}
                    onChange={(e) => handleSortChange(e.target.value)}
                    aria-label="Sorteeropties"
                    className="w-44"
                  />

                  {/* View Toggle */}
                  <div className="flex items-center gap-1 p-1 bg-mv-gray-100 rounded-lg">
                    <Button
                      variant={viewMode === 'list' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      aria-label="Lijstweergave"
                    >
                      <Icon name="menu" className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      aria-label="Rasterweergave"
                    >
                      <Icon name="squares2x2" className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                      Filters wissen
                    </Button>
                  )}
                </div>
              </Card>

              {/* Selection Bar - matches SCR-102 selection_bar */}
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between p-3 bg-mv-blue-50 border border-mv-blue-200 rounded-lg">
                  <span className="text-sm font-medium text-mv-blue-800">
                    {selectedIds.size} document(en) geselecteerd
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                      Deselecteren
                    </Button>
                    {/* GAP-023: Bulk share action */}
                    <Button variant="ghost" size="sm" onClick={handleBulkShare}>
                      <Icon name="share" className="w-4 h-4 mr-1" />
                      Delen
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="arrowDownTray" className="w-4 h-4 mr-1" />
                      Downloaden
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="folder" className="w-4 h-4 mr-1" />
                      Verplaatsen
                    </Button>
                  </div>
                </div>
              )}

              {/* Content - matches SCR-102 states: loading, error, empty */}
              {isLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <Spinner size="lg" />
                </div>
              ) : isError ? (
                <EmptyState
                  icon={<Icon name="exclamationTriangle" className="h-full w-full" />}
                  title="Fout bij laden"
                  description="Er is een fout opgetreden bij het laden van documenten."
                  action={{
                    label: 'Opnieuw proberen',
                    onClick: () => refetch(),
                    variant: 'primary',
                  }}
                />
              ) : documents.length === 0 ? (
                <EmptyState
                  icon={<Icon name="document" className="h-full w-full" />}
                  title="Geen documenten gevonden"
                  description={
                    hasActiveFilters
                      ? 'Pas je filters aan of upload een nieuw document.'
                      : 'Upload je eerste document om te beginnen.'
                  }
                  action={{
                    label: 'Document uploaden',
                    onClick: () => setIsUploadModalOpen(true),
                    variant: 'primary',
                  }}
                  secondaryActions={
                    hasActiveFilters
                      ? [
                          {
                            label: 'Filters wissen',
                            onClick: handleClearFilters,
                            variant: 'secondary',
                          },
                        ]
                      : undefined
                  }
                />
              ) : (
                <>
                  {/* Document Table/Grid - matches SCR-102 document_grid */}
                  {viewMode === 'list' ? (
                    <DataTable
                      data={documents}
                      columns={columns}
                      keyExtractor={(doc) => doc.id}
                      sortBy={sortField}
                      sortOrder={sortOrder}
                      onSort={(key) => {
                        const newOrder = sortField === key && sortOrder === 'asc' ? 'desc' : 'asc';
                        handleSortChange(`${key}:${newOrder}`);
                      }}
                      onRowClick={handleViewDocument}
                      selectedRowId={selectedDocument?.id}
                    />
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {documents.map((doc) => (
                        <DocumentCard
                          key={doc.id}
                          document={doc}
                          isSelected={selectedIds.has(doc.id)}
                          onSelect={handleSelectDocument}
                          onView={handleViewDocument}
                          onShare={handleShareDocument}
                          onDelegate={handleDelegateDocument}
                          onDelete={handleDeleteDocument}
                        />
                      ))}
                    </div>
                  )}

                  {/* Pagination - matches SCR-102 pagination section */}
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-mv-gray-600">
                          Totaal: {totalCount} documenten
                        </span>
                        <Select
                          options={PAGE_SIZE_OPTIONS}
                          value={pageSize.toString()}
                          onChange={(e) => handlePageSizeChange(e.target.value)}
                          className="w-36"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          Vorige
                        </Button>
                        <span className="px-3 text-sm text-mv-gray-600">
                          Pagina {currentPage} van {totalPages}
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={currentPage >= totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          Volgende
                        </Button>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </div>
          </main>
          {/* GAP-004: Document Detail Panel */}
          <DocumentDetailPanel
            document={selectedDocument}
            onClose={handleCloseDetailPanel}
            onShare={handleShareDocument}
            onDelegate={handleDelegateDocument}
            onDelete={handleDeleteDocument}
          />
        </SplitPane>
      ) : (
        <main className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">
          {/* Header - matches SCR-102 header section */}
          <PageHeader
            title="Mijn Documenten"
            breadcrumbs={[
              { label: 'Home', href: '/' },
              { label: 'Documenten', href: '/documents' },
            ]}
            actions={
              <div className="flex items-center gap-2">
                {!isSidebarOpen && (
                  <Button
                    variant="ghost"
                    onClick={() => setIsSidebarOpen(true)}
                    className="md:hidden"
                  >
                    <Icon name="funnel" className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                )}
                {selectedIds.size > 0 && (
                  <Button
                    variant="secondary"
                    onClick={handleBulkDelete}
                    disabled={isDeleting}
                  >
                    <Icon name="trash" className="w-4 h-4 mr-2" />
                    Verwijderen ({selectedIds.size})
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setIsSecureUploadModalOpen(true)}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Icon name="shieldCheck" className="w-4 h-4 mr-2" />
                  Veilig uploaden
                </Button>
                <Button variant="primary" onClick={() => setIsUploadModalOpen(true)}>
                  <Icon name="arrowUpTray" className="w-4 h-4 mr-2" />
                  Document uploaden
                </Button>
              </div>
            }
          />

          {/* Filters - matches SCR-102 filters section */}
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <Input
                  type="search"
                  placeholder="Zoek documenten..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-label="Zoek in documenten"
                />
              </div>

              {/* File Type Filter */}
              <Select
                options={FILE_TYPE_OPTIONS}
                value={fileType}
                onChange={(e) => handleFileTypeChange(e.target.value)}
                aria-label="Filter op bestandstype"
                className="w-36"
              />

              {/* Date Range Filter */}
              <Select
                options={DATE_RANGE_OPTIONS}
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                aria-label="Filter op datumbereik"
                className="w-40"
              />

              {/* Sort */}
              <Select
                options={SORT_OPTIONS}
                value={sortParam}
                onChange={(e) => handleSortChange(e.target.value)}
                aria-label="Sorteeropties"
                className="w-44"
              />

              {/* View Toggle */}
              <div className="flex items-center gap-1 p-1 bg-mv-gray-100 rounded-lg">
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  aria-label="Lijstweergave"
                >
                  <Icon name="menu" className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  aria-label="Rasterweergave"
                >
                  <Icon name="squares2x2" className="w-4 h-4" />
                </Button>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  Filters wissen
                </Button>
              )}
            </div>
          </Card>

          {/* Selection Bar - matches SCR-102 selection_bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between p-3 bg-mv-blue-50 border border-mv-blue-200 rounded-lg">
              <span className="text-sm font-medium text-mv-blue-800">
                {selectedIds.size} document(en) geselecteerd
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                  Deselecteren
                </Button>
                {/* GAP-023: Bulk share action */}
                <Button variant="ghost" size="sm" onClick={handleBulkShare}>
                  <Icon name="share" className="w-4 h-4 mr-1" />
                  Delen
                </Button>
                <Button variant="ghost" size="sm">
                  <Icon name="arrowDownTray" className="w-4 h-4 mr-1" />
                  Downloaden
                </Button>
                <Button variant="ghost" size="sm">
                  <Icon name="folder" className="w-4 h-4 mr-1" />
                  Verplaatsen
                </Button>
              </div>
            </div>
          )}

          {/* Content - matches SCR-102 states: loading, error, empty */}
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : isError ? (
            <EmptyState
              icon={<Icon name="exclamationTriangle" className="h-full w-full" />}
              title="Fout bij laden"
              description="Er is een fout opgetreden bij het laden van documenten."
              action={{
                label: 'Opnieuw proberen',
                onClick: () => refetch(),
                variant: 'primary',
              }}
            />
          ) : documents.length === 0 ? (
            <EmptyState
              icon={<Icon name="document" className="h-full w-full" />}
              title="Geen documenten gevonden"
              description={
                hasActiveFilters
                  ? 'Pas je filters aan of upload een nieuw document.'
                  : 'Upload je eerste document om te beginnen.'
              }
              action={{
                label: 'Document uploaden',
                onClick: () => setIsUploadModalOpen(true),
                variant: 'primary',
              }}
              secondaryActions={
                hasActiveFilters
                  ? [
                      {
                        label: 'Filters wissen',
                        onClick: handleClearFilters,
                        variant: 'secondary',
                      },
                    ]
                  : undefined
              }
            />
          ) : (
            <>
              {/* Document Table/Grid - matches SCR-102 document_grid */}
              {viewMode === 'list' ? (
                <DataTable
                  data={documents}
                  columns={columns}
                  keyExtractor={(doc) => doc.id}
                  sortBy={sortField}
                  sortOrder={sortOrder}
                  onSort={(key) => {
                    const newOrder = sortField === key && sortOrder === 'asc' ? 'desc' : 'asc';
                    handleSortChange(`${key}:${newOrder}`);
                  }}
                  onRowClick={handleViewDocument}
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {documents.map((doc) => (
                    <DocumentCard
                      key={doc.id}
                      document={doc}
                      isSelected={selectedIds.has(doc.id)}
                      onSelect={handleSelectDocument}
                      onView={handleViewDocument}
                      onShare={handleShareDocument}
                      onDelegate={handleDelegateDocument}
                      onDelete={handleDeleteDocument}
                    />
                  ))}
                </div>
              )}

              {/* Pagination - matches SCR-102 pagination section */}
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-mv-gray-600">
                      Totaal: {totalCount} documenten
                    </span>
                    <Select
                      options={PAGE_SIZE_OPTIONS}
                      value={pageSize.toString()}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      className="w-36"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Vorige
                    </Button>
                    <span className="px-3 text-sm text-mv-gray-600">
                      Pagina {currentPage} van {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Volgende
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </main>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        onUploadComplete={handleUploadComplete}
      />

      {/* Secure Upload Modal (with PII scanning) */}
      <SecureDocumentUploadModal
        open={isSecureUploadModalOpen}
        onOpenChange={setIsSecureUploadModalOpen}
        onUploadComplete={handleSecureUploadComplete}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog {...dialogProps} />

      {/* GAP-023: Share Document Modal */}
      <ShareDocumentModal
        open={isShareModalOpen}
        onOpenChange={(open) => {
          setIsShareModalOpen(open);
          if (!open) {
            setDocumentToShare(null);
          }
        }}
        documentId={documentToShare?.id}
        documentIds={selectedIds.size > 0 && !documentToShare ? Array.from(selectedIds) : undefined}
        document={documentToShare || undefined}
      />

      {/* GAP-032: Delegate Access from Document Modal */}
      <DelegateFromDocumentModal
        isOpen={isDelegateModalOpen}
        onClose={() => {
          setIsDelegateModalOpen(false);
          setDocumentToDelegate(null);
        }}
        document={documentToDelegate}
      />
    </div>
  );
}

export default DocumentListView;
